import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import * as Popover from '@radix-ui/react-popover'
import { groupIntentsByHierarchy, getIntentLabel } from '@utils/intents'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useIsCompact } from '@/hooks/useDevice'
import { useSessionStore } from '@/stores'
import { IntentTypeMapping } from '@constants'
import { OVERLAY_FLOATING_Z } from '@utils/overlayStack'

interface SelectItemIntentsProps {
  intents: number[]
  onSelectIntent: (intent: number) => void
  useIntents?: boolean
  item?: any
}

const SelectItemIntents = ({ intents, onSelectIntent, useIntents = false, item }: SelectItemIntentsProps) => {
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const [sheetCategory, setSheetCategory] = useState<string | null>(null)
  const [showAllPrimary, setShowAllPrimary] = useState(false)
  const isCompact = useIsCompact()
  const retentionAuthUrl = useSessionStore(s => s.retentionAuthUrl)

  const groups = groupIntentsByHierarchy(intents)
  const primary = groups._primary?.intents || []
  const displaySize = 2
  const primaryNeedsCollapse = !useIntents && primary.length > displaySize
  const visiblePrimary = primaryNeedsCollapse && !showAllPrimary ? primary.slice(0, displaySize) : primary

  const handleIntentClick = (intent: number) => {
    setOpenCategory(null)
    setSheetCategory(null)

    // Retention demo intercept — when a retention auth URL has been delivered
    // with the session, route the click into the external retention modal
    // instead of submitting the input through the normal path.
    if (retentionAuthUrl && item) {
      const retention = (window as any).SolvpathRetentionLite
      if (retention?.open) {
        retention.open({
          sessionUrl: retentionAuthUrl,
          order_id: item.order?.order_id,
          product_id: item.product?.product_id,
          variant_id: item.variant ? item.variant.variant_id : null,
          intent: IntentTypeMapping[intent],
        })
        return
      }
    }

    onSelectIntent(intent)
  }

  const intentBtnClass = () => `btn-base w-full btn-outline-success item-card-action-btn`

  return (
    <div className="flex flex-col gap-2 w-full">
      {visiblePrimary.map(intent => (
        <button key={intent} type="button" onClick={() => handleIntentClick(intent)} className={intentBtnClass()}>
          {getIntentLabel(item, intent)}
        </button>
      ))}

      {primaryNeedsCollapse && (
        <button
          type="button"
          className="more-options flex items-center justify-center gap-1 text-fluid-sm font-medium py-1"
          onClick={() => setShowAllPrimary(v => !v)}
          style={{ color: 'var(--theme-color)' }}>
          <span>{showAllPrimary ? 'Hide' : 'Show'} More Options</span>
          {showAllPrimary ? <ChevronUp size={13} strokeWidth={1.8} /> : <ChevronDown size={13} strokeWidth={1.8} />}
        </button>
      )}

      {Object.entries(groups)
        .filter(([key]) => key !== '_primary')
        .map(([category, group]) => {
          return (
            <div key={category}>
              {!isCompact ? (
                <Popover.Root
                  open={openCategory === category}
                  onOpenChange={open => setOpenCategory(open ? category : null)}>
                  <Popover.Trigger asChild>
                    <button type="button" className="btn-base btn-outline-success w-full gap-2 item-card-action-btn">
                      {group.label}
                      <ChevronDown
                        size={16}
                        className="transition-transform"
                        style={{ transform: openCategory === category ? 'rotate(180deg)' : 'rotate(0)' }}
                      />
                    </button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      sideOffset={6}
                      align="end"
                      className="intent-dropdown-content shadow-lg animate-fade-in"
                      style={{ zIndex: OVERLAY_FLOATING_Z }}>
                      {group.intents.map(intent => (
                        <button
                          key={intent}
                          type="button"
                          onClick={() => handleIntentClick(intent)}
                          className="intent-dropdown-item w-full text-left transition-colors">
                          {getIntentLabel(item, intent)}
                        </button>
                      ))}
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSheetCategory(category)}
                    className="btn-base btn-outline-success w-full gap-2 item-card-action-btn">
                    {group.label}
                    <ChevronDown size={16} />
                  </button>
                  <BottomSheet
                    isOpen={sheetCategory === category}
                    onClose={() => setSheetCategory(null)}
                    title={group.label}>
                    <div className="flex flex-col gap-2">
                      {group.intents.map(intent => (
                        <button
                          key={intent}
                          type="button"
                          onClick={() => handleIntentClick(intent)}
                          className="intent-dropdown-item w-full text-left transition-colors">
                          {getIntentLabel(item, intent)}
                        </button>
                      ))}
                    </div>
                  </BottomSheet>
                </>
              )}
            </div>
          )
        })}
    </div>
  )
}

export default SelectItemIntents
